import { toast } from 'react-toastify';
import { API } from '../api';
import { getUserDetails } from './orgName';

interface OpenMeetingOptions {
  /** Dosen meeting id — required to run attendance check for Google Meet meetings. */
  meetingId?: string;
  /** Meeting type as stored on the meeting record — only 'Gmeet' triggers attendance API. */
  meetingType?: string;
  /**
   * Scheduled slot, as stored on the call row. For Google Meet these let the
   * review popup wait until everyone left the room or the slot ended — without
   * them the popup opens as soon as the user closes the tab.
   */
  callDate?: string;
  callTime?: string;
  callTimeInterval?: number;
  /** Optional callback fired right before the redirect (e.g. analytics / cleanup). */
  onTabClosed?: () => void;
  /** Override the calendar route (hash route). Defaults to '/app/calendar'. */
  calendarRoute?: string;
}

const POLL_INTERVAL_MS = 1500;
const DEFAULT_CALENDAR_ROUTE = '/app/calendar';

// Google can take a few minutes to publish the conference record, so a check
// fired the instant the tab closes often reports `meetingStarted: false`.
// Retry a bounded number of times before giving up (the 15-min BE job is the
// source of truth either way).
const ATTENDANCE_MAX_ATTEMPTS = 3;
const ATTENDANCE_RETRY_MS = 4000;

/**
 * sessionStorage key + window event used to ask the Calendar page to open the
 * post-meeting review modal for a given meeting. This util can't reach React
 * state directly, so it broadcasts both:
 *   - a CustomEvent — handled immediately when Calendar is already mounted.
 *   - a sessionStorage entry — read on mount when we redirect to Calendar.
 */
export const MEETING_REVIEW_STORAGE_KEY = 'dosen.pendingReviewMeetingId';
export const MEETING_REVIEW_EVENT = 'dosen:open-meeting-review';

/** Ask the Calendar page to open the review modal for `meetingId`. */
export function requestMeetingReview(meetingId: string): void {
  if (!meetingId) return;
  try {
    window.sessionStorage.setItem(MEETING_REVIEW_STORAGE_KEY, meetingId);
  } catch {
    // sessionStorage may be unavailable (private mode / blocked) — the event
    // path still covers the "already on calendar" case.
  }
  window.dispatchEvent(new CustomEvent(MEETING_REVIEW_EVENT, { detail: { meetingId } }));
}

/** Epoch ms when the scheduled slot ends, or null when the row data is incomplete. */
function scheduledCallEnd(callDate?: string, callTime?: string, callTimeInterval?: number): number | null {
  if (!callDate || !callTime || !callTimeInterval) return null;
  const start = new Date(`${callDate}T${callTime}Z`).getTime();
  if (!Number.isFinite(start)) return null;
  return start + callTimeInterval * 60 * 1000;
}

// How often a pending review gate re-checks Google Meet attendance.
const REVIEW_GATE_POLL_MS = 30_000;

/** Pending review gates keyed by meetingId — interval ids, one gate per meeting. */
const pendingReviewGates = new Map<string, number>();

function cancelReviewGate(meetingId: string): void {
  const intervalId = pendingReviewGates.get(meetingId);
  if (intervalId !== undefined) {
    window.clearInterval(intervalId);
    pendingReviewGates.delete(meetingId);
  }
}

/**
 * Holds the review popup for a Google Meet call until either everyone has left
 * the room (`meetingEnded` — Google closes the conference record when the last
 * participant leaves) or the scheduled slot ends, whichever comes first.
 * Each poll also nudges the BE to persist the call's attendance/status, so no
 * separate verify call is needed while a gate is running. Re-opening the
 * meeting (rejoin) cancels the gate; the next tab close re-arms it.
 */
function armReviewGate(meetingId: string, callEndsAt: number): void {
  cancelReviewGate(meetingId);
  const { orgId, location } = getUserDetails();
  let checking = false;

  const tick = async () => {
    if (Date.now() >= callEndsAt) {
      cancelReviewGate(meetingId);
      requestMeetingReview(meetingId);
      return;
    }
    if (checking) return;
    checking = true;
    try {
      const res: any = await API.checkGoogleMeetAttendance(meetingId, orgId, location);
      // has() guards the rejoin race: the gate may have been canceled while
      // this request was in flight.
      if (res?.data?.meetingEnded === true && pendingReviewGates.has(meetingId)) {
        cancelReviewGate(meetingId);
        requestMeetingReview(meetingId);
      }
    } catch {
      // 404 / network — keep waiting; the slot-end cap still fires.
    } finally {
      checking = false;
    }
  };

  const intervalId = window.setInterval(() => void tick(), REVIEW_GATE_POLL_MS);
  pendingReviewGates.set(meetingId, intervalId);
  // First check right away — if everyone already left, no wait at all.
  void tick();
}

/**
 * Silently nudges the BE to verify Google Meet attendance right after the tab
 * closes, so the meeting's Completed/Incomplete status persists promptly (before
 * the 15-min BE cron). No toast — the user only sees a generic "Meeting ended."
 * Google can lag publishing the conference record, so we retry while
 * `meetingStarted` is false. Errors (incl. 404 for non-Gmeet meetings) are
 * swallowed; the cron reconciles attendance as the source of truth.
 */
async function verifyGoogleMeetAttendance(meetingId: string, attempt = 1): Promise<void> {
  const { orgId, location } = getUserDetails();
  try {
    const res: any = await API.checkGoogleMeetAttendance(meetingId, orgId, location);
    if (res?.data?.meetingStarted === false && attempt < ATTENDANCE_MAX_ATTEMPTS) {
      window.setTimeout(() => void verifyGoogleMeetAttendance(meetingId, attempt + 1), ATTENDANCE_RETRY_MS);
    }
  } catch {
    // Non-blocking — 404 (not a Gmeet meeting) or network error.
  }
}

/**
 * Opens the meeting in a new tab and polls until the user closes it. When the
 * tab closes:
 *   - Asks the Calendar page to open the review modal for this meeting (opens
 *     immediately if already on Calendar, otherwise after the redirect below).
 *     Google Meet only: when the scheduled slot is still running, the request
 *     is deferred until everyone has left the room or the slot ends — see
 *     armReviewGate.
 *   - Redirects the current tab to the calendar page.
 *   - For Google Meet meetings: runs the attendance check and shows a status
 *     toast (best-effort, non-blocking).
 *
 * Graceful degradation: an empty link (e.g. an auto-generated Google Meet whose
 * link the BE hasn't filled in yet) or a blocked popup surfaces a toast instead
 * of opening a blank tab / failing silently.
 */
export function openMeetingWithRedirect(meetingLink: string, options: OpenMeetingOptions = {}): void {
  const {
    meetingId,
    meetingType,
    callDate,
    callTime,
    callTimeInterval,
    onTabClosed,
    calendarRoute = DEFAULT_CALENDAR_ROUTE
  } = options;

  // Rejoining a meeting whose review gate is still pending: the user is back in
  // the room, so stop the pending popup — the next tab close re-arms it.
  if (meetingId) cancelReviewGate(meetingId);

  if (!meetingLink) {
    // No link to open. Auto-generated Google Meet rooms get their link filled in
    // by the BE asynchronously, so it can be momentarily empty right after
    // booking — tell the user instead of opening a blank tab.
    if (meetingType === 'Gmeet') {
      toast.info('The Google Meet link is still being generated. Please try again in a moment.');
    } else {
      toast.error('This meeting has no link available.');
    }
    return;
  }

  const meetWindow = window.open(meetingLink, '_blank');
  meetWindow?.focus();

  if (!meetWindow) {
    // Popup blocked or programmatic open denied. The previous silent return left
    // the user clicking with no feedback, so surface it. We still bail without
    // redirect so they can navigate manually.
    toast.warning('Please allow pop-ups for this site to open the meeting.');
    return;
  }

  const intervalId = window.setInterval(() => {
    if (!meetWindow.closed) return;
    window.clearInterval(intervalId);

    // 1. Make sure we land on the calendar page.
    window.location.hash = calendarRoute;

    // 2. Consumer hook (analytics / cleanup).
    try {
      onTabClosed?.();
    } catch {
      // Don't let consumer-side errors block the redirect.
    }

    // 3. Open the review modal for this meeting (now if on Calendar, else after
    //    the redirect). Google Meet with the slot still running: hold it until
    //    everyone left or the slot ends. Any other case opens immediately, with
    //    a background attendance nudge for Gmeet so the status persists promptly.
    if (meetingType === 'Gmeet' && meetingId) {
      toast.info('Meeting ended.');
      const callEndsAt = scheduledCallEnd(callDate, callTime, callTimeInterval);
      if (callEndsAt !== null && Date.now() < callEndsAt) {
        armReviewGate(meetingId, callEndsAt);
      } else {
        requestMeetingReview(meetingId);
        void verifyGoogleMeetAttendance(meetingId);
      }
    } else if (meetingId) {
      requestMeetingReview(meetingId);
    }
  }, POLL_INTERVAL_MS);
}
